import React, { useEffect, useState } from "react";
import "./VersionChecker.css";

interface VersionCheckerProps {
  checkInterval?: number; // in milliseconds, default 5 minutes, minimum 1 minute
  disabled?: boolean; // disable version checking entirely
  debug?: boolean; // show debug info in UI
}

const VersionChecker: React.FC<VersionCheckerProps> = ({
  checkInterval = 5 * 60 * 1000, // 5 minutes
  disabled = false,
  debug = false,
}) => {
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);
  const [currentVersion, setCurrentVersion] = useState<string | null>(null);
  const [lastCheckedVersion, setLastCheckedVersion] = useState<string | null>(
    null
  );

  // Helper function to normalize version strings for comparison
  // Strips pre-release suffixes like -dev, -beta, -rc.1 so only major.minor.patch is compared
  const normalizeVersion = (version: string): string => {
    return version.trim().toLowerCase().replace(/-.*$/, "");
  };

  // Helper function to compare versions and log the comparison
  const compareVersions = (current: string, latest: string): boolean => {
    const normalizedCurrent = normalizeVersion(current);
    const normalizedLatest = normalizeVersion(latest);
    const isDifferent = normalizedCurrent !== normalizedLatest;

    console.log("VersionChecker: Detailed version comparison:");
    console.log("  - Current (raw):", current);
    console.log("  - Current (normalized):", normalizedCurrent);
    console.log("  - Latest (raw):", latest);
    console.log("  - Latest (normalized):", normalizedLatest);
    console.log("  - Are different:", isDifferent);

    return isDifferent;
  };

  useEffect(() => {
    // Skip if disabled
    if (disabled) {
      console.log("VersionChecker: Disabled, skipping version checks");
      return;
    }

    // Ensure minimum check interval of 1 minute
    const safeCheckInterval = Math.max(checkInterval, 60 * 1000);
    if (safeCheckInterval !== checkInterval) {
      console.log(
        "VersionChecker: Check interval adjusted to minimum 1 minute"
      );
    }

    // Get current version from meta tag
    const metaVersion =
      document
        .querySelector('meta[name="app-version"]')
        ?.getAttribute("content") || null;
    setCurrentVersion(metaVersion);

    const checkForUpdates = async () => {
      try {
        // Check if we're running inside React Native WebView
        const isReactNative =
          window.navigator.userAgent.includes("ReactNative");

        if (isReactNative) {
          // React Native app handles version checking, so we skip web-based checks
          console.log(
            "VersionChecker: Skipping check - running in React Native WebView"
          );
          return;
        }

        // Get the current version fresh from DOM each time to avoid closure issues
        const currentMetaVersion =
          document
            .querySelector('meta[name="app-version"]')
            ?.getAttribute("content") || null;

        if (!currentMetaVersion) {
          console.log("VersionChecker: No current version found in meta tag");
          return;
        }

        // For regular web browsers, fetch version.json (same as React Native app)
        const response = await fetch(window.location.origin + "/version.json", {
          cache: "no-cache",
          headers: {
            "Cache-Control": "no-cache, no-store, must-revalidate",
            Pragma: "no-cache",
          },
        });

        if (!response.ok) {
          console.warn(
            "VersionChecker: Failed to fetch version.json, status:",
            response.status
          );
          return;
        }

        const versionData = await response.json();
        const latestVersion = versionData?.version;

        // Store the latest version for comparison
        setLastCheckedVersion(latestVersion);

        if (
          latestVersion &&
          currentMetaVersion &&
          compareVersions(currentMetaVersion, latestVersion)
        ) {
          // Only show banner if we haven't already shown it for this version combination
          if (!showUpdateBanner) {
            console.log(
              "VersionChecker: New version detected, showing update banner"
            );
            setShowUpdateBanner(true);
          } else {
            console.log(
              "VersionChecker: Update banner already shown for this version"
            );
          }
        } else {
          console.log("VersionChecker: Versions match, no update needed");
          // Hide banner if versions now match (shouldn't happen but just in case)
          if (showUpdateBanner) {
            console.log("VersionChecker: Versions now match, hiding banner");
            setShowUpdateBanner(false);
          }
        }
      } catch (error) {
        console.warn("VersionChecker: Failed to check for app updates:", error);
      }
    };

    // Only start checking after we have a current version and a short delay
    // This prevents false positives on initial load
    let intervalId: NodeJS.Timeout | null = null;

    const timeoutId = setTimeout(() => {
      if (metaVersion) {
        console.log(
          "VersionChecker: Starting periodic checks with version:",
          metaVersion
        );
        checkForUpdates(); // Initial check

        // Set up periodic checking
        intervalId = setInterval(checkForUpdates, safeCheckInterval);
      }
    }, 2000); // Wait 2 seconds before starting checks

    return () => {
      clearTimeout(timeoutId);
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [checkInterval, disabled]); // Include disabled flag in dependencies

  const handleUpdateClick = () => {
    // Clear all caches and non-essential localStorage, then reload
    console.log("🔄 VersionChecker: Updating app and clearing caches");

    // Clear browser caches
    if ("caches" in window) {
      caches.keys().then((names) => {
        Promise.all(names.map((name) => caches.delete(name))).then(() => {
          // Clear localStorage except for authentication data
          const preserveKeys = ["mohren_token", "user"];
          const allKeys = Object.keys(localStorage);
          allKeys.forEach((key) => {
            if (!preserveKeys.includes(key)) {
              localStorage.removeItem(key);
            }
          });
          console.log("✅ Caches and localStorage cleared, reloading...");
          (window as any).location.reload();
        });
      });
    } else {
      // Clear localStorage except for authentication data
      const preserveKeys = ["mohren_token", "user"];
      const allKeys = Object.keys(localStorage);
      allKeys.forEach((key) => {
        if (!preserveKeys.includes(key)) {
          localStorage.removeItem(key);
        }
      });
      console.log("✅ localStorage cleared, reloading...");
      (window as any).location.reload();
    }
  };

  const handleDismiss = () => {
    setShowUpdateBanner(false);
  };

  // Debug panel component
  const DebugPanel = () => {
    if (!debug) return null;

    return (
      <div
        style={{
          position: "fixed",
          bottom: "10px",
          right: "10px",
          background: "rgba(0,0,0,0.8)",
          color: "white",
          padding: "10px",
          borderRadius: "5px",
          fontSize: "12px",
          maxWidth: "300px",
          zIndex: 10000,
        }}
      >
        <div>
          <strong>VersionChecker Debug</strong>
        </div>
        <div>Current: {currentVersion || "null"}</div>
        <div>Last Checked: {lastCheckedVersion || "null"}</div>
        <div>Show Banner: {showUpdateBanner ? "true" : "false"}</div>
        <div>Disabled: {disabled ? "true" : "false"}</div>
        <div>Check Interval: {Math.round(checkInterval / 1000)}s</div>
        <div>
          Is React Native:{" "}
          {window.navigator.userAgent.includes("ReactNative")
            ? "true"
            : "false"}
        </div>
      </div>
    );
  };

  return (
    <>
      <DebugPanel />
      {showUpdateBanner && (
        <div className="version-checker-banner">
          <div className="version-checker-content">
            <div className="version-checker-message">
              <span className="version-checker-icon">🔄</span>
              <span>Ein Update der App ist verfügbar!</span>
            </div>
            <div className="version-checker-actions">
              <button
                className="version-checker-btn version-checker-btn-update"
                onClick={handleUpdateClick}
              >
                Aktualisieren
              </button>
              <button
                className="version-checker-btn version-checker-btn-dismiss"
                onClick={handleDismiss}
              >
                Später
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VersionChecker;
